import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { AUTH } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Hospital, ShieldCheck, ShieldAlert, Lock } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState(null); // { token, question }
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [lockout, setLockout] = useState(null); // { retryAfter, message }

  const from = location.state?.from?.pathname || "/";

  const applyChallenge = (payload) => {
    if (payload?.captcha_token && payload?.question) {
      setCaptcha({ token: payload.captcha_token, question: payload.question });
      setCaptchaAnswer("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lockout?.retryAfter > 0) return;
    setLoading(true);
    try {
      await login(email, password, {
        captchaToken: captcha?.token,
        captchaAnswer: captchaAnswer || undefined,
      });
      toast.success("Sesión iniciada correctamente");
      setCaptcha(null);
      setCaptchaAnswer("");
      setLockout(null);
      navigate(from, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data || {};
      const detail = data.detail || "Error de autenticación";
      if (status === 429 || data.code === "TOO_MANY_ATTEMPTS") {
        const seconds = data.retry_after || 900;
        setLockout({
          retryAfter: seconds,
          message: detail,
        });
        setCaptcha(null);
        toast.error(`Cuenta bloqueada temporalmente (${Math.ceil(seconds / 60)} min)`);
      } else if (data.code === "CAPTCHA_REQUIRED" || data.requires_captcha) {
        applyChallenge(data);
        toast.error(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-white border-r border-slate-200 relative overflow-hidden">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-orange-600 text-white flex items-center justify-center">
            <Hospital className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div className="leading-tight">
            <div className="font-semibold tracking-tight">Signature Manager</div>
            <div className="text-xs text-slate-500">Microsoft 365 · Hospital Pediátrico</div>
          </div>
        </div>

        <div className="max-w-md">
          <h1 className="text-4xl font-semibold tracking-tight text-slate-900 leading-tight">
            Firmas corporativas
            <span className="block text-orange-600">centralizadas</span>
          </h1>
          <p className="text-slate-600 mt-4 text-sm leading-relaxed">
            Administra, versiona y publica firmas de correo para todos los usuarios de tu tenant
            Microsoft 365. Sincronización automática desde Microsoft Graph, editor visual y
            despliegue mediante Exchange Transport Rules.
          </p>
          <div className="mt-8 flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
            <ShieldCheck className="w-4 h-4 text-orange-600" strokeWidth={1.75} />
            Solo administradores autorizados
          </div>
        </div>

        <div className="text-xs text-slate-400">
          © 2026 Hospital Pediátrico · Cambiamos la Vida de Nuestros Pacientitos
        </div>

        {/* Decorative pattern */}
        <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-orange-50 -z-0" />
        <div className="absolute -right-40 top-1/3 w-72 h-72 rounded-full bg-orange-100/40 -z-0" />
      </div>

      {/* Right panel: login form */}
      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8 shadow-sm border-slate-200">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-md bg-orange-600 text-white flex items-center justify-center">
              <Hospital className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-semibold tracking-tight">Signature Manager</div>
              <div className="text-xs text-slate-500">Microsoft 365</div>
            </div>
          </div>

          <div className="mb-6">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-600 mb-2">
              Acceso administrativo
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Iniciar sesión</h2>
            <p className="text-sm text-slate-500 mt-1">
              Ingrese sus credenciales para acceder al panel.
            </p>
          </div>

          {lockout?.retryAfter > 0 && (
            <div
              className="mb-4 flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
              data-testid="login-lockout-banner"
            >
              <Lock className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.75} />
              <div className="space-y-1">
                <div className="font-semibold">Acceso bloqueado temporalmente</div>
                <div className="text-red-700 text-xs">
                  {lockout.message} Vuelva a intentar en aproximadamente {Math.ceil(lockout.retryAfter / 60)} minutos.
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                Correo
              </Label>
              <Input
                id="email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={AUTH.emailInput} required
                className="focus-visible:ring-orange-500"
                disabled={loading || lockout?.retryAfter > 0}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                Contraseña
              </Label>
              <Input
                id="password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={AUTH.passwordInput} required
                className="focus-visible:ring-orange-500"
                disabled={loading || lockout?.retryAfter > 0}
              />
            </div>

            {captcha && (
              <div
                className="space-y-1.5 rounded-md border border-orange-200 bg-orange-50 p-3"
                data-testid="login-captcha-block"
              >
                <div className="flex items-center gap-2 text-xs text-orange-700">
                  <ShieldAlert className="w-3.5 h-3.5" strokeWidth={1.75} />
                  <span className="font-semibold uppercase tracking-wider">Verificación adicional</span>
                </div>
                <Label htmlFor="captcha" className="text-sm text-slate-800">
                  {captcha.question}
                </Label>
                <Input
                  id="captcha"
                  inputMode="numeric"
                  autoComplete="off"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  data-testid="login-captcha-input"
                  className="focus-visible:ring-orange-500 bg-white"
                  placeholder="Su respuesta"
                  required
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || lockout?.retryAfter > 0}
              data-testid={AUTH.submitBtn}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white transition-colors duration-150"
            >
              {loading ? "Iniciando..." : "Ingresar"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
